import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ActivityHeatmap = ({ data, loading = false }) => {
  const [selectedCohort, setSelectedCohort] = useState(null);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-muted rounded w-2/3 mb-6"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div key={i} className="h-12 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const getActivityColor = (level) => {
    if (level >= 80) return 'bg-success';
    if (level >= 60) return 'bg-success/70';
    if (level >= 40) return 'bg-warning';
    if (level >= 20) return 'bg-warning/70';
    return 'bg-muted';
  };

  const getActivityLabel = (level) => {
    if (level >= 80) return 'Very High';
    if (level >= 60) return 'High';
    if (level >= 40) return 'Medium';
    if (level >= 20) return 'Low';
    return 'Very Low';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground">Merchant Activity Heatmap</h3>
        <button className="text-xs md:text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1">
          <Icon name="Download" size={14} />
          <span className="hidden sm:inline">Export</span>
        </button>
      </div>
      <div className="space-y-3">
        {data?.map((cohort) => (
          <div 
            key={cohort?.cohort}
            className="group cursor-pointer"
            onClick={() => setSelectedCohort(selectedCohort === cohort?.cohort ? null : cohort?.cohort)}
          >
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <span className="text-xs md:text-sm font-medium text-foreground min-w-[80px] md:min-w-[100px]">
                {cohort?.cohort}
              </span>
              <div className="flex-1 grid grid-cols-7 gap-1 md:gap-2">
                {cohort?.periods?.map((period, index) => (
                  <div
                    key={index}
                    className={`${getActivityColor(period?.activity)} h-8 md:h-10 rounded transition-all duration-250 hover:scale-105 hover:shadow-md relative group/cell`}
                    title={`${period?.period}: ${period?.activity}% activity`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/cell:opacity-100 transition-opacity">
                      <span className="text-xs font-medium text-white">{period?.activity}%</span>
                    </div>
                  </div>
                ))}
              </div>
              <Icon 
                name={selectedCohort === cohort?.cohort ? 'ChevronUp' : 'ChevronDown'} 
                size={16} 
                className="text-muted-foreground flex-shrink-0" 
              />
            </div>

            {selectedCohort === cohort?.cohort && (
              <div className="ml-0 md:ml-[100px] mt-3 p-3 md:p-4 bg-muted/50 rounded-lg space-y-2 animate-in slide-in-from-top-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Total Merchants</p>
                    <p className="text-sm md:text-base font-semibold text-foreground data-text">
                      {cohort?.totalMerchants?.toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Active Rate</p>
                    <p className="text-sm md:text-base font-semibold text-success data-text">
                      {cohort?.activeRate}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Avg. Transactions</p>
                    <p className="text-sm md:text-base font-semibold text-foreground data-text">
                      {cohort?.avgTransactions?.toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <span>Activity Level:</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-muted rounded"></div>
                <span>Very Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-warning/70 rounded"></div>
                <span>Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-warning rounded"></div>
                <span>Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-success/70 rounded"></div>
                <span>High</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-success rounded"></div>
                <span>Very High</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap;